import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';

import { MAIN_DOMAIN } from '@app/app.constants';
import { Pupil, StudentClass, StudentPrivilegeSetting } from '@df/interfaces';

@Injectable({
    providedIn: 'root'
})
export class PupilService {
    public pupil: Pupil;
    public pupilChanged = new EventEmitter<Pupil>();
    public choicedCard;
    public cardChanged = new EventEmitter<any>();
    public currentPupil: Pupil;
    public isRiskCharacterSelected: boolean = false;
    public studentPrivileges: StudentPrivilegeSetting[];
    coachPupil: Pupil;

    private currentClass: StudentClass;

    constructor(private http: HttpClient) {}

    public fetchPupil(id): any {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/people/student/${id}/`).subscribe(pupil => {
            this.pupil = pupil;
            this.pupilChanged.emit(this.pupil);
        });
    }

    public queryPupil(id: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/people/student/${id}/`);
    }

    public nullPupil(): void {
        this.pupil = null;
        this.pupilChanged.emit(null);
    }

    public setStudentPrivileges(studentPrivileges: StudentPrivilegeSetting[] | null): void {
        this.studentPrivileges = studentPrivileges;
    }

    public getStudentPrivileges(): StudentPrivilegeSetting[] | null {
        return this.studentPrivileges;
    }

    public removeStudentPrivileges(): void {
        sessionStorage.removeItem('studentPrivileges');
    }

    public getCachedClass(class_id: number): StudentClass {
        if (this.currentClass && this.currentClass.id === class_id) {
            return this.currentClass;
        }
        return null;
    }

    public getStudentFromClass(student_id: number): Pupil | null {
        if (this.currentClass) {
            for (const student of this.currentClass.students) {
                if (student_id === student.id) {
                    return student;
                }
            }
        }
        return null;
    }

    public setRiskCharacter(isRisk: boolean) {
        this.isRiskCharacterSelected = isRisk;
    }

    public setSelectCharacter(character, isRisk) {
        character['is_risk'] = isRisk;
        sessionStorage.setItem('selectedCharacter', JSON.stringify(character));
    }

    public removeSelectCharacter() {
        if (sessionStorage.getItem('selectedCharacter')) {
            sessionStorage.removeItem('selectedCharacter');
        }
    }

    public getSelectCharacter() {
        return JSON.parse(sessionStorage.getItem('selectedCharacter'));
    }

    public getRiskCharacter(): boolean {
        return this.isRiskCharacterSelected;
    }

    public removeRiskCharacter(): void {
        sessionStorage.removeItem('isRiskCharacterSelected');
    }

    public setCachedClass(student_class: StudentClass): void {
        this.currentClass = student_class;
    }

    public getClasses(): Observable<StudentClass> {
        return this.http.get<StudentClass>(`${MAIN_DOMAIN}/api/people/student/class/`);
    }

    public getPupilClass(class_id: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/people/class/${class_id}/`);
    }

    public getClass(class_id: number): Observable<StudentClass> {
        return this.http.get<StudentClass>(`${MAIN_DOMAIN}/api/people/student/class/${class_id}/`);
    }

    public setCurrentPupil(pupil: Pupil): void {
        this.currentPupil = pupil;
    }

    getCurrentPupil(): Pupil {
        return this.currentPupil;
    }

    set setCoachPupil(pupil: Pupil) {
        this.coachPupil = pupil;
    }

    get getCoachPupil(): Pupil {
        return this.coachPupil;
    }

    containsCyrillicOrLatin(str) {
        // Регулярное выражение для поиска символов 'а-Я' и 'a-Z'
        const regex = /[а-яА-Яa-zA-Z]/;
        return regex.test(str);
    }

    // парсим ответы с содержанием /frac
    // пример: \\frac{1} {2} заменяем на 1/2
    // нужно для того чтобы студенты могли вводить ответы с содержанием /frac
    replaceFrac(answer: string): string {
        let modifyAnswer = answer;
        const regex = /\\frac\s*\{\s*(\d+)\s*\}\s*\{\s*(\d+)\s*\}/g;
        modifyAnswer = modifyAnswer.replace(regex, '$1/$2');
        return modifyAnswer;
    }

    // парсим ответы с содержанием чисел
    // пример: 12 (часов) заменяем на 12
    pullNumber(answer): string {
        const regex = /(\d+)/; // Регулярное выражение для поиска чисел
        const match = answer.match(regex);
        if (match) {
            const number = match[1]; // Первый элемент массива match содержит всю найденную подстроку
            return number;
        }

        return answer;
    }

    // проверка ответов на содержание букв для того чтобы включать встроенную клаву
    checkAnswersPerString(exercise): boolean {
        const shortsAnswers = exercise.answers
            .reduce((accumulator, answ) => {
                return accumulator + (answ.shortAnswer ? this.replaceFrac(answ.shortAnswer) : '') + ', ';
            }, '')
            .slice(0, -2);
        const fullAnswers = exercise.answers
            .reduce((accumulator, answ) => {
                return accumulator + (answ.fullAnswer ? this.replaceFrac(answ.fullAnswer) : '') + ', ';
            }, '')
            .slice(0, -2);
        if (shortsAnswers.length) {
            return this.containsCyrillicOrLatin(shortsAnswers);
        }
        if (fullAnswers.length > 0) {
            return this.containsCyrillicOrLatin(fullAnswers);
        }
        return false;
    }

    // проверка ответов в задании на знаки
    checkAnswerWithSigns(exercise, buttons): Array<string> {
        const shortsAnswers = exercise.answers.map(answ => (answ.shortAnswer ? answ.shortAnswer : ''));
        const signs = ['%', '>', '<', ',', '.', '(', ')', '='];
        const defaultSigns = ['+', '-', '*', '/'];

        for (const sign of signs) {
            if (shortsAnswers.length) {
                for (const short of shortsAnswers) {
                    if (short.includes(sign) && !defaultSigns.includes(sign)) {
                        buttons.push(sign);
                    }
                }
            }
        }

        return buttons;
    }
}
